import { useEffect, useState } from "react";
import api from "../../services/api";

const COLORS = {
  primary: "#0D47A1",
  profit: "#16A34A",
  danger: "#D32F2F",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  muted: "#757575",
};

const PaymentOut = () => {

  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    supplier_id: "",
    amount: "",
    payment_date: new Date().toISOString().slice(0,10),
    remark: ""
  });

  /* ================= LOAD SUPPLIERS ================= */

  const loadSuppliers = async () => {
    try {
      const res = await api.get("/parties/list.php");
      const parties = res.data?.data || [];

      const supplierList = parties.filter(
        p => p.type === "supplier" || p.type === "both"
      );

      setSuppliers(supplierList);

    } catch (err) {
      console.error(err);
    }
  };

  /* ================= LOAD PAYMENTS ================= */

  const loadPayments = async () => {
    try {
      const res = await api.get("/payments-out/list.php");
      setPayments(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadSuppliers();
    loadPayments();
  }, []);

  /* ================= FETCH BALANCE ================= */

  const fetchBalance = async (supplierId) => {
    try {
      const supplier = suppliers.find(s => s.id == supplierId);

      const openingBalance = Number(supplier?.balance || 0);
      const openingAdvance = Number(supplier?.advance_amount || 0);

      // net = payable - advance
      const net = openingBalance - openingAdvance;

      setBalance(net);

    } catch (err) {
      console.error(err);
      setBalance(0);
    }
  };

  useEffect(() => {
    if (form.supplier_id) {
      fetchBalance(form.supplier_id);
    }
  }, [form.supplier_id]);

  const paidAmount = Number(form.amount || 0);
  const remainingBalance = balance - paidAmount;

  /* ================= SUBMIT ================= */

  const submit = async (e) => {

    e.preventDefault();

    const paidAmount = Number(form.amount);

    if (!form.supplier_id || !paidAmount) {
      alert("Supplier and amount required");
      return;
    }

    if (paidAmount <= 0) {
      alert("Amount must be greater than zero");
      return;
    }

    try {

      setLoading(true);

      await api.post("/payments-out/create.php", {
        supplier_id: Number(form.supplier_id),
        amount: paidAmount,
        payment_date: form.payment_date,
        remark: form.remark
      });

      setForm({
        supplier_id: "",
        amount: "",
        payment_date: new Date().toISOString().slice(0,10),
        remark: ""
      });

      setBalance(0);

      loadSuppliers();
      loadPayments();

    } catch (err) {
      console.error(err);
      alert("Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (

    <div style={styles.page}>

      <div style={styles.header}>
        <h2 style={styles.title}>Payment Out</h2>
        <div style={styles.subtitle}>Record supplier payment</div>
      </div>

      <div style={styles.card}>

        <form onSubmit={submit}>

          <select
            value={form.supplier_id}
            onChange={(e) =>
              setForm({ ...form, supplier_id: e.target.value })
            }
            style={styles.input}
          >
            <option value="">Select Supplier</option>

            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}

          </select>

          {form.supplier_id && (
            <div style={styles.summaryCard}>
              <div style={styles.summaryRow}>
                <span>Total Balance</span>
                <span style={styles.totalValue}>
                  ₹ {Number(balance || 0).toFixed(2)}
                </span>
              </div>

              <div style={styles.summaryRow}>
                <span>Remaining After Payment</span>
                <span
                  style={{
                    ...styles.balanceValue,
                    color:
                      remainingBalance > 0
                        ? COLORS.danger
                        : COLORS.profit,
                  }}
                >
                  ₹ {Math.abs(remainingBalance).toFixed(2)}{" "}
                  {remainingBalance < 0 ? "(Advance)" : ""}
                </span>
              </div>
            </div>
          )}

          <input
            type="number"
            placeholder="Paid Amount"
            value={form.amount}
            onChange={(e) =>
              setForm({ ...form, amount: e.target.value })
            }
            style={styles.input}
          />

          <input
            type="date"
            value={form.payment_date}
            onChange={(e) =>
              setForm({ ...form, payment_date: e.target.value })
            }
            style={styles.input}
          />

          <input
            type="text"
            placeholder="Remark (optional)"
            value={form.remark}
            onChange={(e) =>
              setForm({ ...form, remark: e.target.value })
            }
            style={styles.input}
          />

          <button style={styles.saveBtn} disabled={loading}>
            {loading ? "Saving..." : "Pay Supplier"}
          </button>

        </form>

      </div>

      <div style={{ ...styles.card, marginTop: 30 }}>

        <h3 style={{ marginBottom: 15 }}>
          Supplier Payment History
        </h3>

        <div style={styles.tableWrap}>

          <table style={styles.table}>

            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Supplier</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Balance / Advance</th>
                <th style={styles.th}>Remark</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>

            <tbody>

              {payments.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.td}>
                    No payments found
                  </td>
                </tr>
              )}

              {payments.map((p, index) => (
                <tr key={p.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.td}>{p.supplier_name}</td>

                  <td style={styles.td}>
                    ₹ {Number(p.amount || 0).toFixed(2)}
                  </td>

                  <td style={styles.td}>
                    ₹ {Number(p.net_amount || 0).toFixed(2)}{" "}
                    {p.status === "advance" ? "(Advance)" : ""}
                  </td>

                  <td style={styles.td}>
                    {p.remark || "-"}
                  </td>

                  <td style={styles.td}>{p.payment_date}</td>
                </tr>
              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );
};

const styles = {
  page: {
    background: COLORS.bg,
    padding: "10px clamp(15px,4vw,30px) 30px",
    minHeight: "100vh",
  },
  header: {
    marginBottom: 30,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
  },
  subtitle: {
    color: COLORS.muted,
  },
  card: {
    background: COLORS.card,
    borderRadius: 18,
    padding: "25px 30px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 6,
    border: `1px solid ${COLORS.border}`,
    marginBottom: 14,
  },
  summaryCard: {
    background: "#F9FAFB",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  totalValue: { fontWeight: 600 },
  balanceValue: { fontWeight: 600 },
  saveBtn: {
    background: COLORS.primary,
    color: "#fff",
    padding: "10px 16px",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
  tableWrap: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 700,
  },
  th: {
    padding: 12,
    textAlign: "left",
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.muted,
  },
  td: {
    padding: 12,
    borderBottom: `1px solid ${COLORS.border}`,
  },
};

export default PaymentOut;